/**
 * @fileoverview Evaluation View Model
 * @description Projects parsed evaluation reports onto a single table model
 *              shared by every view in the module.
 * @module features/AIEvaluationResults/utils/evaluationView
 */

/**
 * Classify a normalized 0-1 score into a display band.
 * @param {number|null} score - Normalized score.
 * @returns {string} Band identifier.
 */
export function bandForScore(score) {
  if (score === null || score === undefined) {
    return 'not-run';
  }
  if (score >= 0.85) {
    return 'green';
  }
  if (score >= 0.75) {
    return 'amber';
  }
  return 'red';
}

/**
 * Build the stable identity of a row across runs.
 * @param {Object} row - Result row.
 * @returns {string} Comparison key.
 */
export function evaluatorKey(row) {
  return `${row.report}|${row.field}|${row.evaluator}`.toLowerCase();
}

/**
 * Project a parsed report onto the scoreboard model.
 * Scenario runs contribute one row per scenario, scored on its overall result.
 * @param {Object} report - Parsed evaluation report.
 * @returns {Object} Report shaped for the results table.
 */
export function toEvaluatorView(report) {
  if (report.format === 'evaluator') {
    return report;
  }

  const evaluators = report.scenarios.map((scenario) => ({
    id: scenario.scenarioId,
    report: scenario.scenarioId,
    field: scenario.categoryLabel,
    evaluator: 'Overall score',
    framework: scenario.model || scenario.provider || 'Evaluation harness',
    approach: scenario.evaluationMode === 'ai' ? 'LLM-as-a-Judge' : scenario.evaluationMode,
    score: scenario.scores.overall / 100,
    band: bandForScore(scenario.scores.overall / 100),
    metrics: {
      goalCoverage: scenario.scores.goalCoverage / 100,
      responseQuality: scenario.scores.responseQuality / 100,
      efficiency: scenario.scores.efficiency / 100,
    },
    passed: scenario.passed,
    turns: scenario.turns,
    detail: scenario.aiSummary,
  }));

  const scored = evaluators.filter((item) => item.score !== null);

  const conversations = Object.fromEntries(report.scenarios.map((scenario) => [scenario.scenarioId, {
    purpose: scenario.purpose,
    endGoal: scenario.endGoal,
    evaluationMode: scenario.evaluationMode,
    passed: scenario.passed,
    turns: scenario.turns,
    model: scenario.model,
    coveredFacts: scenario.coveredFacts,
    missingFacts: scenario.missingFacts,
    responses: scenario.responses,
    referenceSources: Array.isArray(scenario.referenceSources) ? scenario.referenceSources : [],
  }]));

  return {
    ...report,
    title: 'Scenario Evaluation Results',
    workspace: 'AI Evaluation',
    description: 'One row per evaluated scenario, scored on goal coverage, response quality, and efficiency.',
    evaluators,
    conversations,
    sources: [`${report.scenarios.length} scenarios · ${report.summary.passed} passed · ${report.summary.failed} failed`],
    summary: {
      overallAverage: scored.length ? scored.reduce((sum, item) => sum + item.score, 0) / scored.length : 0,
      evaluatorsScored: scored.length,
      notRun: evaluators.length - scored.length,
      belowTarget: scored.filter((item) => item.score < 0.75).length,
      totalEvaluators: evaluators.length,
      passed: report.summary.passed,
      failed: report.summary.failed,
      passRate: report.summary.passRate,
    },
  };
}
